import { Box, HStack, Spinner, VStack } from '@chakra-ui/react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';
import { Typography } from 'Tokens';
import { ExcelFileIcon } from 'Tokens/Icons/FileIcons';
import { useEsrsReportData } from '../../Report/DocGenerator/ReportDocs.hooks';
import { QUARTERS_FIELDS, QuartersType, YearObject } from '../../DisclosureRequirements';
import { useMemo } from 'react';
import { GetTasksByAssessmentQuery_, QuestionType_Enum_ } from 'models';
import { getMetricRefNumber } from '../../DisclosureRequirements/Metrics/Metrics.hooks';
import { EsrsReportMetric } from '../../Report/Report.types';
import { createTaskReference } from 'containers/Esrs/utils';

type excelObj = {
  standardTitle: string;
  standardRef: string;
  drRef: string;
  drTitle: string;
  metricTitle: string;
  metricRefNumber: string;
  globalOrder: string;
  isMaterial: string;
  framework: string;
  dataCollection: string;
  type: string;
  tags: string;
};
type ExcelWorksheet = ExcelJS.Worksheet;

const excelColumns = [
  {
    header: 'Standard Title',
    key: 'standardTitle',
    width: 30,
  },
  {
    header: 'Standard Reference',
    key: 'standardRef',
    width: 20,
  },
  {
    header: 'Disclosure Requirement Reference',
    key: 'drRef',
    width: 20,
  },
  {
    header: 'Disclosure Requirement Title',
    key: 'drTitle',
    width: 30,
  },

  { header: 'Metric Title', key: 'metricTitle', width: 30 },
  {
    header: 'Ref Number',
    key: 'metricRefNumber',
    width: 25,
  },
  {
    header: 'Global Order',
    key: 'globalOrder',
    width: 20,
  },
  {
    header: 'Is Material',
    key: 'isMaterial',
    width: 20,
  },
  {
    header: 'Framework',
    key: 'framework',
    width: 20,
  },
  {
    header: 'Data Collection',
    key: 'dataCollection',
    width: 25,
  },
  {
    header: 'Frequency',
    key: 'frequency',
    width: 15,
  },
  {
    header: 'Period',
    key: 'period',
    width: 15,
  },
  {
    header: 'Type',
    key: 'type',
    width: 15,
  },

  {
    header: 'Answer',
    key: 'answer',
    width: 30,
  },
  {
    header: 'Tags',
    key: 'tags',
    width: 15,
  },
  {
    header: 'Owner',
    key: 'owner',
    width: 15,
  },
];

const getOwner = (
  tasks: GetTasksByAssessmentQuery_['Task'],
  assessmentId: string,
  item: EsrsReportMetric,
  timeframe: QuartersType | 'Year'
): string => {
  const taskReference = createTaskReference({
    assessmentId,
    metricRef: item.metric?.metricRef,
    reportingUnitId: item.reportingUnitId ?? '',
    timeframe,
    tagCombination: 'None',
  });

  return tasks.find((task) => task.taskReference === taskReference)?.owner?.displayName || '';
};

const addQuarterlyRows = (
  worksheet: ExcelWorksheet,
  excelObj: excelObj,
  item: EsrsReportMetric,
  tasks: GetTasksByAssessmentQuery_['Task'],
  assessmentId: string,
  result?: YearObject
) => {
  QUARTERS_FIELDS.forEach((quarter) => {
    const value = result && !result['isAnswerNotPresent'] ? String(result[quarter]) : '';

    const answerRows: Record<string, any> = {};
    if (value !== undefined) {
      answerRows.answer = value;
      answerRows.frequency = 'Quarterly';
      answerRows.period = quarter;
    }

    const owner = getOwner(tasks, assessmentId, item, quarter);

    worksheet.addRow({
      ...excelObj,
      ...answerRows,
      owner,
    });
  });
};

const addYearlyRows = (
  worksheet: ExcelWorksheet,
  excelObj: excelObj,
  item: EsrsReportMetric,
  tasks: GetTasksByAssessmentQuery_['Task'],
  assessmentId: string,
  metricType?: QuestionType_Enum_ | null,
  result?: YearObject
) => {
  const value =
    metricType === QuestionType_Enum_.Decimal_
      ? result && !result['isAnswerNotPresent']
        ? String(result['Year'])
        : ''
      : (item?.textAnswer ?? '');

  const answerRows: Record<string, any> = {};
  if (value !== undefined) {
    answerRows.answer = value;
    answerRows.frequency = 'Yearly';
    answerRows.period = 'Year';
  }

  const owner = getOwner(tasks, assessmentId, item, 'Year');

  worksheet.addRow({
    ...excelObj,
    ...answerRows,
    owner,
  });
};

export const AnswersExcelDownloader = () => {
  const { esrsAssessmentId = '' } = useParams();

  const { data: esrsData, loading: esrsLoading } = useEsrsReportData(true, true);

  const allMaterialMetricsData = useMemo(
    () =>
      esrsData.categories
        .flatMap((category) => category.standards)
        .flatMap((standard) => standard.disclosureRequirements)
        .flatMap((dr) => dr.metrics)
        .filter((metric) => !metric.hasOptedOut),
    [esrsData]
  );

  const handleDownload = async () => {
    if (!allMaterialMetricsData || !allMaterialMetricsData?.length) {
      alert('No data available to download.');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Data');
    worksheet.columns = excelColumns;

    worksheet.columns.forEach((column) => {
      column.style = { alignment: { wrapText: true, vertical: 'top' } };
    });

    const sortedArray = [...allMaterialMetricsData].sort((a, b) => {
      const aStandardRef = a.metric?.materialStandard?.standardRef || '';
      const bStandardRef = b.metric?.materialStandard?.standardRef || '';
      const standardOrder = aStandardRef.localeCompare(bStandardRef);

      const aRequirementOrder = a?.metric?.metric?.requirement?.order ?? 0;
      const bRequirementOrder = b.metric?.metric?.requirement?.order ?? 0;
      const requirementOrder = aRequirementOrder - bRequirementOrder;

      const aGlobalOrder = a.metric?.metric?.globalOrder || '';
      const bGlobalOrder = b.metric?.metric?.globalOrder || '';
      const globalOrder = aGlobalOrder.localeCompare(bGlobalOrder);

      return globalOrder || standardOrder || requirementOrder;
    });

    sortedArray.forEach((item) => {
      const frequency = item?.metric?.frequency ?? '';
      const result = item?.tableData?.result;
      const metricType = item?.metric?.metric?.metricType;
      const excelObj = {
        standardTitle: item.metric?.metric?.requirement?.group?.standard?.title || '',
        standardRef: item.metric?.materialStandard?.standardRef || '',
        drRef: item.metric?.metric?.disclosureRequirementRef || '',
        drTitle: item.metric?.metric?.requirement?.title || '',
        metricTitle: item.metric?.metric?.title || '',
        metricRefNumber: getMetricRefNumber(item?.metric?.metric) || '',
        globalOrder: item.metric?.metric?.globalOrder || '',
        isMaterial: item?.metric?.isMaterial ? 'yes' : 'no',
        framework: item.metric.metric.requirement.frameworkRef || '',
        dataCollection: item.metric?.dataCollection || '',
        type: metricType || '',
        tags: Array.isArray(item?.metric?.materialMetricTags?.[0]?.materialTagValues)
          ? item.metric?.materialMetricTags[0].materialTagValues.map((a) => a.tagValue).join(', ')
          : '',
      };

      if (frequency === 'Quarterly')
        addQuarterlyRows(worksheet, excelObj, item, esrsData.tasks, esrsAssessmentId, result);
      else if (frequency === 'Yearly')
        addYearlyRows(
          worksheet,
          excelObj,
          item,
          esrsData.tasks,
          esrsAssessmentId,
          metricType,
          result
        );
    });

    try {
      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `Answers_Data_${esrsAssessmentId}-${new Date().toISOString()}.xlsx`);
    } catch (error) {
      console.error('Error generating Excel file:', error);
      alert('Failed to generate Excel file. Please try again later.');
    }
  };

  if (esrsLoading) {
    return (
      <HStack w="100%" spacing="10.33px" cursor="default">
        <Box position="relative">
          <Spinner boxSize="16px" position="absolute" opacity={0.46} top="22%" left="15%" />
          <Box opacity={0.1}>
            <ExcelFileIcon />
          </Box>
        </Box>

        <VStack alignItems="start" spacing="0px" opacity={0.46}>
          Generating:
          <Typography variant="bodyStrong">Answers export</Typography>
          <Typography variant="detail">Excel document</Typography>
        </VStack>
      </HStack>
    );
  }

  return (
    <HStack
      w="100%"
      spacing="10.33px"
      onClick={handleDownload}
      cursor="pointer"
      _hover={{ opacity: 0.8 }}
    >
      <ExcelFileIcon />
      <VStack alignItems="start" spacing="0px">
        <Typography variant="bodyStrong">Answers export</Typography>
        <Typography variant="detail">Excel document</Typography>
      </VStack>
    </HStack>
  );
};
